"use client";

import { publicStorePath } from "@/lib/storefront/public-url";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import type { CategorySlug } from "@/lib/category/types";

export interface SwitcherCategory {
	slug: CategorySlug | "all";
	label: string;
	count: number;
	icon?: ReactNode;
}

interface CategorySwitcherProps {
	categories: SwitcherCategory[];
	/** `"all"` when on /peptides hub, otherwise the detail slug. */
	active: CategorySlug | "all";
	locale: string;
	channel: string;
}

export function CategorySwitcher({ categories, active, locale, channel: _channel }: CategorySwitcherProps) {
	const t = useTranslations("plp");
	const activeRef = useRef<HTMLAnchorElement | null>(null);

	useEffect(() => {
		activeRef.current?.scrollIntoView({
			behavior: "smooth",
			inline: "center",
			block: "nearest",
		});
	}, [active]);

	return (
		<div className="relative border-b border-border bg-white">
			{/* Faint blueprint raster — datasheet feel */}
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0 opacity-[0.04]"
				style={{
					backgroundImage:
						"linear-gradient(to right, var(--primary) 1px, transparent 1px), linear-gradient(to bottom, var(--primary) 1px, transparent 1px)",
					backgroundSize: "48px 48px",
				}}
			/>
			<div className="relative mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:py-6">
				{/* Eyebrow */}
				<div className="mb-3 flex items-center gap-3 font-mono text-[0.62rem] uppercase tracking-[0.22em] text-primary">
					<span aria-hidden className="bg-primary/60 block h-px w-8" />
					{t("peptideCategories")}
				</div>

				<div className="-mx-4 flex touch-manipulation gap-2 overflow-x-auto px-4 pb-1 [-webkit-overflow-scrolling:touch] [mask-image:linear-gradient(to_right,transparent,black_16px,black_calc(100%-16px),transparent)] [scroll-snap-type:x_mandatory] sm:-mx-6 sm:px-6 md:mx-0 md:flex-wrap md:[mask-image:none]">
					{categories.map((cat) => {
						const isActive = cat.slug === active;
						const href =
							cat.slug === "all"
								? `${publicStorePath(locale, "/peptides")}`
								: `${publicStorePath(locale, `/categories/${cat.slug}`)}`;
						return (
							<Link
								key={cat.slug}
								href={href}
								ref={isActive ? activeRef : undefined}
								aria-current={isActive ? "page" : undefined}
								className={
									"group relative inline-flex min-h-[48px] shrink-0 items-center gap-2.5 whitespace-nowrap rounded-md border px-4 py-2.5 text-[13px] font-medium tracking-tight transition-all duration-200 [scroll-snap-align:start] " +
									(isActive
										? "border-primary bg-primary text-white shadow-[0_8px_24px_-12px_rgba(8,39,74,0.45)]"
										: "hover:border-primary/40 hover:bg-primary/[0.03] border-border bg-white text-foreground hover:text-primary")
								}
							>
								{cat.icon && (
									<span
										className={
											"flex h-5 w-5 shrink-0 items-center justify-center " +
											(isActive ? "text-white/90" : "text-primary/80")
										}
									>
										{cat.icon}
									</span>
								)}
								<span className="font-mono text-[0.7rem] uppercase leading-none tracking-[0.14em]">
									{cat.label}
								</span>
								<span aria-hidden className={"block h-3 w-px " + (isActive ? "bg-white/25" : "bg-border")} />
								<span
									className={
										"font-mono text-[0.66rem] tabular-nums tracking-[0.04em] " +
										(isActive ? "text-white/80" : "text-muted-foreground")
									}
								>
									{String(cat.count).padStart(2, "0")}
								</span>
							</Link>
						);
					})}
				</div>
			</div>
		</div>
	);
}
